import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { ScoreRing, colorOf } from '@/components/ScoreRing';
import { TimeField } from '@/components/TimeField';
import { ChevronLeft, Utensils, Sunrise, Lightbulb, Check, Timer } from 'lucide-react';
import { format } from 'date-fns';

export default function FastingLog() {
  const nav = useNavigate();
  const today = format(new Date(), 'yyyy-MM-dd');
  const [lastMeal, setLastMeal] = useState('20:15');
  const [firstMeal, setFirstMeal] = useState('11:30');
  const [log, setLog] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/fasting-logs', { params: { date: today } }).then(r => {
      if (r.data) { setLog(r.data); setLastMeal(r.data.last_meal_time); setFirstMeal(r.data.first_meal_time); }
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.post('/fasting-logs', { date: today, last_meal_time: lastMeal, first_meal_time: firstMeal });
      setLog(r.data); toast.success('Fasting saved'); nav(-1);
    } catch { toast.error('Failed to save'); } finally { setSaving(false); }
  };

  const fastingHours = log ? log.fasting_min / 60 : 0;

  return (
    <div className="space-y-5" data-testid="fasting-log-page">
      <div className="flex items-center gap-3">
        <button onClick={() => nav(-1)} data-testid="back-btn" className="w-9 h-9 rounded-full bg-[var(--c-film)] flex items-center justify-center"><ChevronLeft className="w-5 h-5" /></button>
        <h1 className="font-display font-black text-2xl">Fasting Log</h1>
      </div>
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-full bg-[#00C8FF]/15 flex items-center justify-center"><Timer className="w-5 h-5 text-[#00C8FF]" /></div>
        <div><div className="font-display font-bold">Today</div><div className="text-xs text-muted-foreground">{format(new Date(), 'EEEE, MMM d')}</div></div>
      </div>

      <div className="regen-card-elevated p-5 space-y-4">
        <div className="font-display font-bold">1. Enter Your Fasting Times</div>
        <div className="grid grid-cols-2 gap-4">
          <TimeField icon={Utensils} label="Last Meal" value={lastMeal} onChange={setLastMeal} testid="fasting-last-meal" />
          <TimeField icon={Sunrise} label="First Meal" value={firstMeal} onChange={setFirstMeal} testid="fasting-first-meal" />
        </div>
        <button onClick={save} disabled={saving} data-testid="save-fasting-btn" className="w-full rounded-xl py-3 bg-[#00C8FF] text-[#081726] font-bold tracking-wide active:scale-[0.99] transition disabled:opacity-60">
          {saving ? 'SAVING…' : 'SAVE FASTING'}
        </button>
      </div>

      {log && (
        <>
          {/* Fasting ring */}
          <div className="regen-card-elevated p-5 flex items-center gap-5" data-testid="fasting-ring">
            <ScoreRing value={log.fasting_min / 60} max={16} color={colorOf(log.fasting_color)} size={120} stroke={11}
              center={<div className="flex flex-col items-center"><div className="font-mono text-xl font-bold">{log.fasting_duration}</div><div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">Fasting</div></div>} />
            <div className="flex-1">
              <div className="text-sm font-semibold" style={{ color: colorOf(log.fasting_color) }}>{log.fasting_label}</div>
              <div className="text-xs text-muted-foreground mt-1">
                {fastingHours < 10 ? 'Aim for a longer fast to unlock fat-burning.' : fastingHours < 15 ? 'Solid window. Push toward 15h for peak benefits.' : "You're in the sweet spot for fat loss & metabolic health."}
              </div>
            </div>
          </div>

          <div className="regen-card-elevated p-5" data-testid="fasting-summary">
            <div className="font-display font-bold mb-3">Fasting Summary</div>
            <div className="grid grid-cols-3 gap-3 text-center">
              <Stat label="Fasting" value={log.fasting_duration} sub={log.fasting_label} subColor={colorOf(log.fasting_color)} />
              <Stat label="Fasting Score" value={log.fasting_score} sub={log.fasting_label} subColor={colorOf(log.fasting_color)} />
              <Stat label="Meal Timing" value={log.meal_timing_score} sub={log.meal_timing_label} subColor={colorOf(log.meal_timing_color)} />
            </div>
          </div>

          {/* Meal timing highlight */}
          <div className="regen-card-elevated p-4 flex items-center justify-between" data-testid="meal-timing-card">
            <div>
              <div className="text-xs text-muted-foreground">Last Meal Time</div>
              <div className="font-mono text-xl mt-0.5" style={{ color: colorOf(log.meal_timing_color) }}>{log.last_meal_clock}</div>
            </div>
            <div className="text-right">
              <div className="text-xs text-muted-foreground">Eating Window</div>
              <div className="font-mono text-xl mt-0.5">{log.eating_window}</div>
              <div className="text-[11px] text-muted-foreground">{log.first_meal_clock} – {log.last_meal_clock}</div>
            </div>
          </div>

          <div className="regen-card-elevated p-4 flex items-start gap-3">
            <Lightbulb className="w-5 h-5 text-[#00C8FF] flex-shrink-0 mt-0.5" />
            <div>
              <div className="text-sm font-semibold text-[#00C8FF]">Fasting Tip</div>
              <div className="text-sm text-muted-foreground mt-0.5">
                {log.meal_timing_score >= 75 ? 'Great meal timing! Finishing dinner earlier improves sleep, insulin sensitivity and fat loss.' : 'Your last meal is late. Moving it before 8:00 PM could meaningfully boost recovery and sleep quality.'}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, sub, subColor }) {
  return (
    <div>
      <div className="label-mini">{label}</div>
      <div className="font-mono text-2xl mt-1">{value}</div>
      {sub && <div className="text-xs font-semibold mt-0.5" style={{ color: subColor }}>{sub}</div>}
    </div>
  );
}
